<script setup lang="ts">
import { cloneDeep } from 'lodash'
import { computed, ref, VNode, watch } from 'vue'

import { getOppositeParticipant } from '@collector/shared-ui-domain'
import {
  HandballGoalAttribute,
  SubParticipantPosition,
} from '@collector/sportsapi-client-legacy'
import { useIncidentGroupComponents } from '@mobile/views/Relation/DetailsPanel/Modals/IncidentDetailsModal/composables/useIncidentGroupComponents'
import PlayerSelectGroup from '@mobile/views/Relation/DetailsPanel/Modals/IncidentDetailsModal/PlayerSelectGroup.vue'
import { useLineupsSelectOptions } from '@mobile/views/Relation/Shared/Lineups/useLineupsSelectOption'
import { ProbableLinkedIncident } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'

import { DetailButtonsConfiguration } from '../../../composables/useSportOptions'
import { useThrowPositionDetailsModalForm } from '../components/ThrowPositionDetailsModal/composables'
import ThrowPositionDetailsModal from '../components/ThrowPositionDetailsModal/ThrowPositionDetailsModal.vue'
import { GroupName } from '../consts'
import {
  goalPositionAttributeMap,
  groups,
  pitchDistanceAttributeMap,
  pitchPositionAttributeMap,
} from './consts'

interface Props {
  linkedIncident: ProbableLinkedIncident
}

const props = defineProps<Props>()

// data
const localLinkedIncident = cloneDeep(props.linkedIncident)

// composables
const { probableHomeParticipant, probableAwayParticipant } =
  useEventInjections()
const { findParticipant } = useLineupsSelectOptions(
  ref(localLinkedIncident.incident),
)
const incidentGroupComponentsComposable = useIncidentGroupComponents(
  localLinkedIncident,
  groups,
)
const {
  updateIncident: _updateIncident,
  addGroupAttribute,
  addAttribute,
  removeAttribute,
  selectedAttributes,
} = incidentGroupComponentsComposable
const { assistant, goalkeeper, pitchDistance, pitchPosition, goalPosition } =
  useThrowPositionDetailsModalForm(
    localLinkedIncident,
    removeAttribute,
    addAttribute,
    pitchDistanceAttributeMap,
    pitchPositionAttributeMap,
    goalPositionAttributeMap,
    () => {
      const styleGroup = groups.get(
        GroupName.Style,
      ) as DetailButtonsConfiguration<HandballGoalAttribute>

      addGroupAttribute(styleGroup, HandballGoalAttribute.Air)
    },
  )

// data
const participant = localLinkedIncident.participant!
const oppositeParticipant = getOppositeParticipant(
  participant.id,
  probableHomeParticipant.value,
  probableAwayParticipant.value,
)

// computed
const isPenaltyGoalAttributeSelected = computed(() => {
  return selectedAttributes.value.includes(HandballGoalAttribute.PenaltyGoal)
})

const isGoalkeeperImportant = computed(() => {
  return !selectedAttributes.value.includes(HandballGoalAttribute.EmptyNetGoal)
})

const isThrowPositionFulfilled = computed(() => {
  return !!(
    goalPosition.value &&
    ((pitchDistance.value && pitchPosition.value) ||
      isPenaltyGoalAttributeSelected.value)
  )
})

// emits
const emit = defineEmits(['close'])

// methods
function updateIncident(): void {
  const assistant_id = assistant.value
  const assistant_name = findParticipant(assistant.value)?.name
  const goalkeeper_id = selectedAttributes.value.includes(
    HandballGoalAttribute.EmptyNetGoal,
  )
    ? null
    : goalkeeper.value

  _updateIncident({ assistant_id, assistant_name, goalkeeper_id })

  emit('close')
}

function getGroupComponent(name: GroupName): VNode | null {
  return incidentGroupComponentsComposable.getGroupComponent(groups.get(name)!)
}

// watchers
watch(isPenaltyGoalAttributeSelected, (isSelected) => {
  if (isSelected) {
    pitchDistance.value = null
    pitchPosition.value = null
  }
})
</script>

<template>
  <ThrowPositionDetailsModal
    v-model:goalPosition="goalPosition"
    v-model:pitchDistance="pitchDistance"
    v-model:pitchPosition="pitchPosition"
    :linkedIncident="localLinkedIncident"
    :isThrowPositionFulfilled
    :pitchPositionDisabled="isPenaltyGoalAttributeSelected"
    pitchPositionName="Goal and shot position"
    @close="$emit('close')"
    @save="updateIncident"
  >
    <template #prePitchComponent>
      <div class="space-y-4">
        <PlayerSelectGroup
          v-model="goalkeeper"
          :important="isGoalkeeperImportant"
          :disabled="!isGoalkeeperImportant"
          name="Goalkeeper"
          nullable
          :filterByPosition="SubParticipantPosition.Goalkeeper"
          :isFulfilled="!!goalkeeper"
          :participant="oppositeParticipant"
        />

        <PlayerSelectGroup
          v-model="assistant"
          name="Assistant"
          nullable
          :isFulfilled="!!assistant"
          :participant="localLinkedIncident.participant!"
        />
      </div>
    </template>

    <component :is="getGroupComponent(GroupName.Style)" />
    <component :is="getGroupComponent(GroupName.Type)" />
  </ThrowPositionDetailsModal>
</template>
